import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useApiMutation } from "@/hooks/useApiMutation";
import { showSuccess } from "@/lib/toastHelpers";


const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "INR", name: "Indian Rupee" },
  { code: "PHP", name: "Philippine Peso" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "MXN", name: "Mexican Peso" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "KRW", name: "South Korean Won" },
  { code: "THB", name: "Thai Baht" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "VND", name: "Vietnamese Dong" },
  { code: "BRL", name: "Brazilian Real" },
  { code: "ZAR", name: "South African Rand" },
  { code: "RUB", name: "Russian Ruble" },
  { code: "TRY", name: "Turkish Lira" },
  { code: "AED", name: "UAE Dirham" },
  { code: "SAR", name: "Saudi Riyal" },
];

const TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Brussels",
  "Europe/Vienna",
  "Europe/Stockholm",
  "Europe/Moscow",
  "Europe/Istanbul",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Singapore",
  "Asia/Manila",
  "Asia/Jakarta",
  "Asia/Kuala_Lumpur",
  "Asia/Ho_Chi_Minh",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Australia/Brisbane",
  "Pacific/Auckland",
];

interface WorkspaceRegionalPreferencesTabProps {
  workspaceId: string;
  isTeamWorkspace: boolean;
  isOwner: boolean;
}

export function WorkspaceRegionalPreferencesTab({ 
  workspaceId, 
  isTeamWorkspace,
  isOwner 
}: WorkspaceRegionalPreferencesTabProps) {
  
  const { user, refreshUser } = useAuth();
  const [currency, setCurrency] = useState("USD");
  const [timezone, setTimezone] = useState("UTC");
  const [unitSystem, setUnitSystem] = useState("metric");
  const initialLoadDone = useRef(false);

  // For personal workspace, get preferences from user object
  // For team workspace, fetch from API
  const { data: teamPreferences, isLoading } = useQuery({
    queryKey: [`/api/team/${workspaceId}/preferences`],
    queryFn: async () => {
      return apiRequest("GET", `/api/team/${workspaceId}/preferences`) as unknown as { currency: string; timezone: string; unitSystem: string };
    },
    enabled: isTeamWorkspace,
  });

  const preferences = isTeamWorkspace ? teamPreferences : {
    currency: user?.currency || "USD",
    timezone: user?.timezone || "UTC",
    unitSystem: user?.unitSystem || "metric",
  };

  // Sync preferences when they change (initial load or after refresh)
  useEffect(() => {
    if (preferences && !initialLoadDone.current) {
      setCurrency(preferences.currency || "USD");
      setTimezone(preferences.timezone || "UTC");
      setUnitSystem(preferences.unitSystem || "metric");
      initialLoadDone.current = true;
    }
  }, [preferences]);

  const updateMutation = useApiMutation({
    endpoint: (vars: any) => isTeamWorkspace ? `/api/team/${workspaceId}/preferences` : `/api/auth/settings`,
    method: "PATCH",
    successMessage: "Preferences updated successfully",
    onSuccess: async (data: any) => {
      if (isTeamWorkspace) {
        queryClient.invalidateQueries({ queryKey: [`/api/team/${workspaceId}/preferences`] });
      } else {
        if (data.preferences) {
          setCurrency(data.preferences.currency);
          setTimezone(data.preferences.timezone);
          setUnitSystem(data.preferences.unitSystem);
        }
        refreshUser();
      }
    },
  });

  const handleSave = () => {
    updateMutation.mutate({ currency, timezone, unitSystem });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // Team preferences not yet implemented
  if (isTeamWorkspace) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Regional Preferences</CardTitle>
          <CardDescription>
            Team regional preferences are coming soon. For now, each team member uses their personal workspace preferences.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const canEdit = isTeamWorkspace ? isOwner : true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Regional Preferences</CardTitle>
        <CardDescription>
          {isTeamWorkspace 
            ? "Set default currency, timezone, and unit system for your team workspace."
            : "Set your personal workspace preferences for currency, timezone, and unit system."
          }
          {isTeamWorkspace && !isOwner && (
            <span className="block mt-2 text-amber-600">
              Only workspace owners can modify these settings.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency</Label>
          <Select value={currency} onValueChange={setCurrency} disabled={!canEdit}>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((curr) => (
                <SelectItem key={curr.code} value={curr.code}>
                  {curr.code} - {curr.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Timezone</Label>
          <Select value={timezone} onValueChange={setTimezone} disabled={!canEdit}>
            <SelectTrigger id="timezone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map((tz) => (
                <SelectItem key={tz} value={tz}>
                  {tz}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="unitSystem">Unit System</Label>
          <Select value={unitSystem} onValueChange={setUnitSystem} disabled={!canEdit}>
            <SelectTrigger id="unitSystem">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="metric">Metric (kg, cm, °C)</SelectItem>
              <SelectItem value="imperial">Imperial (lb, in, °F)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
